import { API } from 'aws-amplify';
import { FormSchemas } from './FormSchemas';
import { getCorporateUser, getAllCorporateUsers } from '../CorporateQueries';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode 
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === 'DEV' ? "Node-API" : "prod-api");

// Get current user email
const getCurrentUserEmail = () => {
  try {
    const user = localStorage.getItem('user');
    if (!user) return '';
    const parsed = JSON.parse(user);
    return parsed.email || parsed || '';
  } catch {
    return localStorage.getItem('user') || '';
  }
};

// Get all master forms
export const getAllMasterForms = async () => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getAllMasterForms',
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return { forms: [] };
  } catch (error) {
    console.error('Error getting all master forms:', error);
    return { forms: [] };
  }
};

// Create a new master form
export const createMasterForm = async (formData) => {
  try {
    const now = new Date().toISOString();
    const currentUser = getCurrentUserEmail();
    const formId = `form_${formData.form_type}_${Date.now()}`;
    
    // Get the schema for this form type
    const formSchema = FormSchemas[formData.form_type] || {};
    
    const payload = {
      command: 'createMasterForm',
      id: formId,
      form_name: formData.form_name,
      form_type: formData.form_type, // 'annual' or 'probation'
      description: formData.description || '',
      form_schema: JSON.stringify(formSchema),
      start_date: formData.start_date || now,
      end_date: formData.end_date || now,
      status: formData.status || 'draft',
      assigned_employees: JSON.stringify(formData.assigned_employees || []),
      program_code: formData.program_code || '',
      created_by: currentUser,
      updated_by: currentUser,
      created_at: now,
      updated_at: now,
    };
    
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error creating master form:', error);
    throw error;
  }
};

// Update a master form
export const updateMasterForm = async (formData) => {
  try {
    const currentUser = getCurrentUserEmail();
    const payload = {
      command: 'updateMasterForm',
      id: formData.id,
      form_name: formData.form_name,
      form_type: formData.form_type,
      description: formData.description || '',
      form_schema: formData.form_schema || JSON.stringify({}),
      start_date: formData.start_date,
      end_date: formData.end_date,
      status: formData.status || 'draft',
      assigned_employees: JSON.stringify(formData.assigned_employees || []),
      program_code: formData.program_code || '',
      updated_by: currentUser,
      updated_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error updating master form:', error);
    throw error;
  }
};

// Delete a master form
export const deleteMasterForm = async (formId) => {
  try {
    const payload = {
      command: 'deleteMasterForm',
      id: formId,
    };
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error deleting master form:', error);
    throw error;
  }
};

// Duplicate a master form
export const duplicateMasterForm = async (sourceFormId, newFormData) => {
  try {
    const now = new Date().toISOString();
    const currentUser = getCurrentUserEmail();
    const newFormId = `form_${newFormData.form_type}_${Date.now()}`;
    
    // Get the source form first
    const sourceForm = await getMasterForm(sourceFormId);
    
    const payload = {
      command: 'createMasterForm',
      id: newFormId,
      form_name: newFormData.form_name,
      form_type: newFormData.form_type,
      description: sourceForm.description || '',
      form_schema: sourceForm.form_schema || JSON.stringify(FormSchemas[newFormData.form_type] || {}),
      start_date: newFormData.start_date || now,
      end_date: newFormData.end_date || now,
      status: 'draft',
      assigned_employees: '[]',
      program_code: sourceForm.program_code || '',
      created_by: currentUser,
      updated_by: currentUser,
      created_at: now,
      updated_at: now,
    };
    
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error duplicating master form:', error);
    throw error;
  }
};

// Get a single master form
export const getMasterForm = async (formId) => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getMasterForm',
        id: formId,
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return null;
  } catch (error) {
    console.error('Error getting master form:', error);
    return null;
  }
};

// Assign employees to a form
export const assignEmployeesToForm = async (formId, employeeEmails, periodStart, periodEnd) => {
  try {
    const currentUser = getCurrentUserEmail();
    
    // First update the form's assigned employees
    const payload = {
      command: 'assignEmployeesToForm',
      id: formId,
      assigned_employees: JSON.stringify(employeeEmails),
      updated_by: currentUser,
      updated_at: new Date().toISOString(),
    };
    
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    
    // Also update the form duration
    await updateFormDuration(formId, periodStart, periodEnd);
    
    return response;
  } catch (error) {
    console.error('Error assigning employees to form:', error);
    throw error;
  }
};

// Update form duration
export const updateFormDuration = async (formId, startDate, endDate) => {
  try {
    const currentUser = getCurrentUserEmail();
    const payload = {
      command: 'updateFormDuration',
      id: formId,
      start_date: startDate,
      end_date: endDate,
      updated_by: currentUser,
      updated_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error updating form duration:', error);
    throw error;
  }
};

// Update form status
export const updateFormStatus = async (formId, status) => {
  try {
    const currentUser = getCurrentUserEmail();
    const payload = {
      command: 'updateMasterFormStatus',
      id: formId,
      status: status,
      updated_by: currentUser,
      updated_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error updating form status:', error);
    throw error;
  }
};

// Get form assignments for an employee
export const getEmployeeFormAssignments = async (email) => {
  try {
    const currentUserEmail = String(email || getCurrentUserEmail()).toLowerCase().trim();
    
    // Get all master forms
    const formsRes = await getAllMasterForms();
    const allForms = formsRes.forms || [];
    
    // Filter forms assigned to this employee
    const employeeForms = [];
    
    allForms.forEach(form => {
      let assignedEmployees = [];
      
      // Parse assigned_employees
      try {
        if (typeof form.assigned_employees === 'string') {
          assignedEmployees = JSON.parse(form.assigned_employees);
        } else if (Array.isArray(form.assigned_employees)) {
          assignedEmployees = form.assigned_employees;
        }
      } catch (error) {
        console.error('Error parsing assigned_employees:', error);
      }
      
      // Check if current user is in assigned employees
      const isAssigned = assignedEmployees.some(emp => {
        let empEmail = '';
        if (typeof emp === 'string') {
          empEmail = emp;
        } else if (typeof emp === 'object' && emp !== null) {
          empEmail = emp.email || emp.employee_email || '';
        }
        return String(empEmail).toLowerCase().trim() === currentUserEmail;
      });
      
      if (isAssigned) {
        employeeForms.push(form);
      }
    });
    
    return { forms: employeeForms };
  } catch (error) {
    console.error('Error getting employee form assignments:', error);
    return { forms: [] };
  }
};

// Get forms where current user is a manager (reporting_l1 or reporting_l2)
export const getManagerFormAssignments = async (email) => {
  try {
    const currentUserEmail = (email || getCurrentUserEmail()).toLowerCase().trim();
    
    // Get the current user's role
    let currentUserRole = '';
    try {
      const currentUserData = await getCorporateUser(currentUserEmail);
      currentUserRole = currentUserData?.role || '';
    } catch (e) {
      console.error('Error getting current user role:', e);
    }
    
    // Check if user has elevated access (Program Manager or HR)
    const hasElevatedAccess = ['Program Manager', 'HR'].includes(currentUserRole);
    
    // Helper to unwrap DynamoDB values
    const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
    const safeJSON = (v) => {
      const raw = unwrap(v);
      if (!raw) return null;
      if (typeof raw === 'object') return raw;
      try { return JSON.parse(raw); } catch { return null; }
    };
    
    // Get all master forms
    const formsRes = await getAllMasterForms();
    const allForms = formsRes.forms || [];

    // Get live corporate users to have up-to-date reporting relationships
    let allCorporateUsers = [];
    try {
      const usersResult = await getAllCorporateUsers();
      allCorporateUsers = Array.isArray(usersResult) ? usersResult : [];
    } catch (e) {
      console.error('Error fetching corporate users for reporting lookup:', e);
    }
    // Build a quick lookup by email
    const corpUserMap = {};
    allCorporateUsers.forEach(u => {
      if (u.email) corpUserMap[String(u.email).toLowerCase().trim()] = u;
    });
    
    // Get all probation reviews to check submission status
    const reviewsRes = await API.post(api_name, '', {
      body: {
        command: 'getAllPerformanceReviews',
        reviewType: 'probation',
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    
    let allReviews = [];
    if (reviewsRes?.status === 200 && reviewsRes?.data) {
      try {
        const parsed = JSON.parse(reviewsRes.data.body);
        allReviews = parsed.reviews || parsed.Items || [];
      } catch {
        allReviews = reviewsRes.data.reviews || reviewsRes.data.Items || [];
      }
    }
    
    // Filter and flatten forms to show one row per employee assignment
    const managerForms = [];
    
    allForms.forEach(form => {
      let assignedEmployees = [];
      
      // Parse assigned_employees
      try {
        if (typeof form.assigned_employees === 'string') {
          assignedEmployees = JSON.parse(form.assigned_employees);
        } else if (Array.isArray(form.assigned_employees)) {
          assignedEmployees = form.assigned_employees;
        }
      } catch (error) {
        console.error('Error parsing assigned_employees:', error);
      }
      
      // Check if current user is manager of ANY employee in this form
      // OR if user has elevated access (Program Manager / HR)
      // Check both the snapshot in assigned_employees AND the live corporate users table
      const isManagerOfForm = hasElevatedAccess || assignedEmployees.some(emp => {
        if (typeof emp === 'object') {
          const empEmail = String(emp.email || emp.employee_email || '').toLowerCase().trim();
          // Check snapshot reporting fields
          const snapshotL1 = String(emp.reporting_l1 || '').toLowerCase().trim();
          const snapshotL2 = String(emp.reporting_l2 || '').toLowerCase().trim();
          if (snapshotL1 === currentUserEmail || snapshotL2 === currentUserEmail) return true;
          // Also check live data
          const liveUser = empEmail ? corpUserMap[empEmail] : null;
          if (liveUser) {
            const liveL1 = String(liveUser.reporting_l1 || '').toLowerCase().trim();
            const liveL2 = String(liveUser.reporting_l2 || '').toLowerCase().trim();
            if (liveL1 === currentUserEmail || liveL2 === currentUserEmail) return true;
          }
        }
        return false;
      });
      
      // If user has access to this form, filter employees appropriately
      if (isManagerOfForm) {
        assignedEmployees.forEach(emp => {
          // For elevated access (Program Manager / HR), show all employees
          // For regular managers, only show employees where they are L1 or L2
          if (!hasElevatedAccess) {
            const empEmail = String(emp?.email || emp?.employee_email || '').toLowerCase().trim();
            const snapshotL1 = String(emp?.reporting_l1 || '').toLowerCase().trim();
            const snapshotL2 = String(emp?.reporting_l2 || '').toLowerCase().trim();
            const liveUser = empEmail ? corpUserMap[empEmail] : null;
            const liveL1 = liveUser ? String(liveUser.reporting_l1 || '').toLowerCase().trim() : '';
            const liveL2 = liveUser ? String(liveUser.reporting_l2 || '').toLowerCase().trim() : '';
            const isMyEmployee =
              snapshotL1 === currentUserEmail || snapshotL2 === currentUserEmail ||
              liveL1 === currentUserEmail || liveL2 === currentUserEmail;
            if (!isMyEmployee) {
              return; // Skip - user is not their manager
            }
          }
          let empEmail = '';
          let empName = '';
          
          if (typeof emp === 'string') {
            empEmail = emp;
            empName = emp;
          } else if (typeof emp === 'object') {
            empEmail = emp.email || emp.employee_email || '';
            empName = emp.name || emp.employee_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || empEmail;
          }
          
          // Find the review for this employee and form
          const review = allReviews.find(r => {
            const rEmail = String(unwrap(r.email) || '').toLowerCase().trim();
            const rPeriodStart = unwrap(r.period_start) || '';
            const rId = unwrap(r.id) || '';
            // Match by either email+periodStart or by ID format
            const emailMatch = rEmail === empEmail.toLowerCase().trim() && rPeriodStart === form.start_date;
            const idMatch = rId === `${empEmail}_probation_${form.start_date}`;
            return emailMatch || idMatch;
          });
          
          // Check if employee has submitted
          let employeeSubmitted = false;
          if (review) {
            const userResp = safeJSON(review.user_response);
            if (userResp) {
              // Check if response has actual data (either direct fields or nested responses)
              const responses = userResp.responses || userResp;
              employeeSubmitted = responses && Object.keys(responses).some(k => 
                k !== 'schema' && k !== 'submittedAt' && responses[k] && String(responses[k]).trim() !== ''
              );
            }
          }
          
          // Check if manager has submitted
          let managerSubmitted = false;
          if (review) {
            const mgrResp = safeJSON(review.manager_response);
            if (mgrResp) {
              // Check if response has actual data
              const responses = mgrResp.responses || mgrResp;
              managerSubmitted = responses && Object.keys(responses).some(k => 
                k !== 'schema' && k !== 'submittedAt' && k !== 'submittedBy' && k !== 'S' && 
                responses[k] && String(responses[k]).trim() !== ''
              );
            }
          }
          
          managerForms.push({
            ...form,
            employee_email: empEmail,
            employee_name: empName,
            employee_submitted: employeeSubmitted,
            manager_submitted: managerSubmitted,
          });
        });
      }
    });
    
    return managerForms;
  } catch (error) {
    console.error('Error getting manager form assignments:', error);
    return [];
  }
};

// Get all form assignments (for managers) - using getAllMasterForms with filtering
export const getAllFormAssignments = async () => {
  try {
    const res = await getAllMasterForms();
    const forms = res.forms || [];
    
    // Transform to assignments format
    const assignments = [];
    forms.forEach(form => {
      const assignedEmployees = JSON.parse(form.assigned_employees || '[]');
      assignedEmployees.forEach(email => {
        assignments.push({
          id: `${form.id}_${email}`,
          form_id: form.id,
          form_name: form.form_name,
          form_type: form.form_type,
          employee_email: email,
          employee_name: email, // Could be enhanced with actual name lookup
          period_start: form.start_date,
          period_end: form.end_date,
          status: form.status,
        });
      });
    });
    
    return { assignments };
  } catch (error) {
    console.error('Error getting all form assignments:', error);
    return { assignments: [] };
  }
};

// Remove employee assignment from a form
export const removeEmployeeAssignment = async (assignmentId) => {
  try {
    // assignmentId format: "formId_employeeEmail"
    const [formId, employeeEmail] = assignmentId.split('_', 2);
    
    // Get current form
    const form = await getMasterForm(formId);
    if (!form) throw new Error('Form not found');
    
    // Remove employee from assigned list
    const assignedEmployees = JSON.parse(form.assigned_employees || '[]');
    const updatedEmployees = assignedEmployees.filter(email => email !== employeeEmail);
    
    // Update form
    const currentUser = getCurrentUserEmail();
    const payload = {
      command: 'assignEmployeesToForm',
      id: formId,
      assigned_employees: JSON.stringify(updatedEmployees),
      updated_by: currentUser,
      updated_at: new Date().toISOString(),
    };
    
    const response = await API.post(api_name, '', { 
      body: payload, 
      response: true, 
      convertEmptyValues: true, 
      headers: {} 
    });
    return response;
  } catch (error) {
    console.error('Error removing employee assignment:', error);
    throw error;
  }
};
